import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import _without from 'lodash/without';
import { useSessionStorage } from 'usehooks-ts';
import { Access, AccessLevelEnum } from 'graphql-common';
import { ToastContainer } from '@lib/components/Toast/Toast';
import LogoLoader from '@lib/components/LogoLoader/LogoLoader';
import {
  GetUrlParams,
  PATH_AUTH,
  PATH_ERROR,
  PATH_INTERVENTIONS,
} from '@lib/enums/urls';
import generateSafeRedirect from '@lib/utils/generateSafeRedirect';
import { setSentryTags } from '@lib/utils/sentryHelpers';
import UpdateHtmlLang from '@lib/components/UpdateHtmlLang/UpdateHtmlLang';
import { checkUserAccessForPage } from '@lib/utils/accesses';
import useWhoamiQueryHook from 'utils/fetch/hooks/useWhoamiQueryHook';
import { APP_URLS } from 'constants/urls';
import getHomePageUrl from 'layouts/DashboardLayout/utils/getHomePageUrl';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import { ANIMATION_LOADER_AVAILABLE } from '@lib/enums/localStorageKeys';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import getStatusFromError from '@lib/utils/getStatusFromError';

const { MODE, VITE_AUTH_PLATFORM_URL: AUTH_PLATFORM } = import.meta.env;

function ProtectedRoute() {
  const { pathname, search } = useLocation();
  const { i18n } = useTranslation();
  const urlSearchParams = new URLSearchParams(search);
  const dst = urlSearchParams.get(GetUrlParams.Dst) as string;
  const isDst = dst && dst !== '/';
  const pathnameArr = _without(pathname.split('/'), '');
  const pathBase = pathnameArr.length ? pathnameArr[0] : '/';

  const [showLogo, setShowLogo] = useSessionStorage(
    ANIMATION_LOADER_AVAILABLE,
    true,
  );
  const shouldShowLogo = showLogo && pathBase !== PATH_AUTH && !isDst;

  // Whoami
  const { data, loading, error, currentUserLang, firstLoading } =
    useWhoamiQueryHook();
  const userId = _get(data, 'whoami.resource.id');
  const userEmail = _get(data, 'whoami.resource.email');
  const username = _get(data, 'whoami.resource.username');
  const deactivatedAt = _get(data, 'whoami.resource.deactivatedAt');
  const userType = _get(data, 'whoami.resource.__typename');
  const setup = _get(data, 'whoami.resource.setup');
  const finalAccesses = _get(data, 'whoami.resource.finalAccesses', []);
  const companyAccesses = _get(
    data,
    'whoami.resource.company.plan.accesses',
    [],
  ) as Access[];
  const graphQLErrors = _get(error, 'graphQLErrors', []);
  const authError = graphQLErrors.find(
    ({ extensions }) => extensions?.code === 'AUTHENTICATION_ERROR',
  );
  const isUserAuth = !authError && !!userId;
  const isUserDeactivated = !!deactivatedAt;
  const loginUrl = `${AUTH_PLATFORM}/${PATH_AUTH}/login`;

  const userAccessForPage = checkUserAccessForPage(finalAccesses, pathname);
  const companyAccessForPage = checkUserAccessForPage(
    companyAccesses,
    pathname,
  );
  const defaultUrlAfterAuth = getHomePageUrl(finalAccesses);

  if (MODE !== 'development' && !loading) {
    setSentryTags({ isUserAuth, userId, userEmail, userType, username });
  }

  const appRender = () => (
    <QueryParamProvider adapter={ReactRouter6Adapter}>
      <UpdateHtmlLang />
      <Outlet />
      <ToastContainer />
    </QueryParamProvider>
  );

  const appNavigate = (to: string, external?: boolean, dstUrl?: string) => {
    if (!external) return <Navigate to={to} replace />;
    const toUrlSearch = dstUrl && dstUrl !== '/' ? `?dst=${dstUrl}` : '';
    window.location.href = `${to}${toUrlSearch}`;
    return undefined;
  };

  useEffect(() => {
    if (i18n.language !== currentUserLang) {
      i18n.changeLanguage(currentUserLang);
    }
  }, [i18n, currentUserLang]);

  useEffect(() => {
    if (firstLoading && shouldShowLogo) {
      const timer = setTimeout(() => {
        setShowLogo(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [firstLoading, setShowLogo, shouldShowLogo]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem(ANIMATION_LOADER_AVAILABLE, 'true');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [setShowLogo]);

  if (shouldShowLogo) {
    return <LogoLoader setVisible={setShowLogo} />;
  }
  if (firstLoading) {
    return <CircleLoader />;
  }
  if (pathBase === PATH_AUTH) {
    if (dst && dst !== '/') {
      window.location.href = dst;
      return undefined;
    }
    return appNavigate(APP_URLS.dashboard.index.path);
  }
  if (pathBase === PATH_ERROR && !error) {
    return appNavigate(APP_URLS.dashboard.index.path);
  }
  if (pathBase === PATH_ERROR && authError) {
    appNavigate(loginUrl, true);
    return undefined;
  }
  if (pathBase === PATH_ERROR && error) {
    return (
      <ErrorPage
        status={getStatusFromError(error)}
        description={error?.message}
        logoUrl={APP_URLS.dashboard.index.path}
        fullHeight
      />
    );
  }
  if (error && !authError) {
    return appNavigate(APP_URLS.error.path);
  }
  if (!isUserAuth) {
    return <CircleLoader />;
  }
  if (!setup) {
    const dstUrl = generateSafeRedirect(pathname + search);
    appNavigate(loginUrl, true, dstUrl.toString());
    return undefined;
  }
  if (isUserDeactivated) {
    if (pathname !== APP_URLS.dashboard.unauthorized.path) {
      return appNavigate(APP_URLS.dashboard.unauthorized.path);
    }
  }
  if (!isUserDeactivated && pathname === APP_URLS.dashboard.unauthorized.path) {
    return appNavigate(APP_URLS.dashboard.notFound.path);
  }
  if (
    companyAccessForPage === AccessLevelEnum.LimitedRead &&
    pathname.includes(`/${PATH_INTERVENTIONS}`) &&
    !APP_URLS.dashboard.interventions.index.isTheSameUrlAs(pathname)
  ) {
    return appNavigate(APP_URLS.dashboard.interventions.index.path);
  }
  if (userAccessForPage === AccessLevelEnum.LimitedRead) {
    return appNavigate(APP_URLS.dashboard.accessDenied.path);
  }
  if (isUserAuth && !pathnameArr.length) {
    return appNavigate(defaultUrlAfterAuth);
  }
  return appRender();
}

export default ProtectedRoute;
